import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Typography, Link, List, ListItem, Paper, Grid, ListItemText, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../styles';
import LinkIcon from '@mui/icons-material/Link';

const StyledSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  backgroundColor: colors.white,
  marginBottom: theme.spacing(3),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  position: 'relative'
}));

const FloatingMenu = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(4),
  backgroundColor: colors.white,
  width: '100%',
  marginRight: theme.spacing(4),
  alignSelf: 'flex-start',
  boxShadow: 'none',
  borderRadius: '20px',
  padding: theme.spacing(2)
}));

const MenuLink = styled(ListItem)(({ theme, active }) => ({
  borderRadius: '0.5rem',
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: 'transform 80ms cubic-bezier(0, 0.3, 1, 0.5), background 80ms cubic-bezier(0, 0.3, 1, 0.5)',
  '&:hover': {
    backgroundColor: active ? `${theme.palette.primary.main}20` : 'rgba(0, 0, 0, 0.04)',
    borderRadius: '0.5rem'
  },
  '&:active': {
    transform: 'scale(0.95)'
  },
  ...(active && {
    backgroundColor: `${theme.palette.primary.main}20`
  }),
  '& .MuiTypography-root': {
    fontWeight: active ? 900 : 600,
    fontSize: '1.1rem'
  }
}));

const Section = ({ id, title, children, onVisible }) => {
  const sectionRef = useRef(null);
  const [shouldObserve, setShouldObserve] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShouldObserve(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldObserve) return;
    const node = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          onVisible && onVisible(id);
        }
      },
      { threshold: 0.5, rootMargin: '-80px 0px' }
    );
    if (node) observer.observe(node);
    return () => { if (node) observer.unobserve(node); };
  }, [id, onVisible, shouldObserve]);

  return (
    <StyledSection ref={sectionRef}>
      <div id={id} style={{ position: 'absolute', top: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.primary }}>
          {title}
        </Typography>
        <Tooltip title="Copy link" placement="left">
          <IconButton
            size="small"
            aria-label={`Copy link to ${title}`}
            onClick={() => {
              try {
                const origin = window.location.origin;
                const path = window.location.pathname;
                const url = `${origin}${path}#${id}`;
                navigator.clipboard.writeText(url);
              } catch (error) {
                console.error('Failed to copy section link', error);
              }
            }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ color: colors.secondary }}>{children}</Box>
    </StyledSection>
  );
};

const ContestRules = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isManualScroll, setIsManualScroll] = useState(false);
  const scrollTimeout = useRef(null);

  // Get current month name for dynamic display
  const currentMonth = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[new Date().getMonth()];
  }, []);

  const sections = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'eligibility', label: 'Eligibility' },
    { id: 'no-purchase-necessary', label: 'No Purchase Necessary' },
    { id: 'contest-period', label: 'Contest Period' },
    { id: 'how-to-enter', label: 'How to Enter' },
    { id: 'scoring', label: 'Scoring & Judging' },
    { id: 'prizes', label: 'Prizes' },
    { id: 'winner-selection', label: 'Winner Selection' },
    { id: 'disqualification', label: 'Disqualification' },
    { id: 'taxes', label: 'Taxes' },
    { id: 'general-conditions', label: 'General Conditions' },
    { id: 'privacy', label: 'Privacy' },
    { id: 'disputes', label: 'Disputes' },
    { id: 'sponsor', label: 'Sponsor' }
  ], []);

  const handleSectionVisible = useCallback((id) => {
    if (isManualScroll) return;
    setActiveSection(id);
    try {
      const path = window.location.pathname;
      window.history.replaceState(null, '', `${path}#${id}`);
    } catch (error) {
      console.warn('Unable to update URL hash', error);
    }
  }, [isManualScroll]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    setIsManualScroll(true);
    setActiveSection(sectionId);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.scrollBy({ top: -80, behavior: 'smooth' });
    scrollTimeout.current = setTimeout(() => setIsManualScroll(false), 1000);
    try {
      const path = window.location.pathname;
      window.history.replaceState(null, '', `${path}#${sectionId}`);
    } catch (error) {
      console.warn('Unable to update URL hash', error);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  const SideMenu = () => (
    <FloatingMenu>
      <List>
        {sections.map((s) => (
          <MenuLink key={s.id} active={activeSection === s.id} onClick={() => scrollToSection(s.id)} disableRipple>
            <ListItemText 
              primary={s.label}
              primaryTypographyProps={{
                fontWeight: activeSection === s.id ? 900 : 600,
                fontSize: '0.95rem',
                color: 'inherit'
              }}
            />
          </MenuLink>
        ))}
      </List>
    </FloatingMenu>
  );

  return (
    <Box sx={{
      backgroundColor: colors.background,
      minHeight: '100vh',
      py: { xs: 6, md: 10 },
      overflowY: 'auto',
      height: '100vh',
      position: 'fixed',
      width: '100%'
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, color: colors.primary, mb: 1 }}>
            Monthly Budgeting Challenge
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary, mb: 1 }}>
            Official Contest Rules
          </Typography>
          <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.8 }}>
            Last updated: August 13, 2026 &nbsp;|&nbsp; Rules Version: 1.2
          </Typography>
        </Box>

        <Grid container spacing={0}>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
            <SideMenu />
          </Grid>

          <Grid item xs={12} md={9} sx={{ pr: { xs: 0, md: 4 } }}>

        <Section id="overview" title="1. Overview" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            The Budgit Monthly Budgeting Challenge (the "Contest") is a skill-based competition. Each calendar month, eligible participants compete on a public leaderboard. The top-ranked participant at the end of the contest period may receive a cash prize if a prize is offered for that period, as described in Section 7.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <strong>This is a skill-based contest, not a sweepstakes, lottery, or game of chance. Winners are determined by their rank on the monthly leaderboard as calculated under Section 6 (period XP and the listed tie-breakers). Sponsor does not use a drawing, raffle, or other chance-based selection to determine winners. No element of chance is involved in determining winners.</strong>
          </Typography>
        </Section>

        <Section id="eligibility" title="2. Eligibility" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            To be eligible to participate and win:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You must be at least 18 years of age (or the age of majority in your jurisdiction, whichever is greater).</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You must be a legal resident of a jurisdiction where participation in skill-based contests for prizes is permitted by law.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You must have an active Budgit account in good standing.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You must not have opted out of leaderboard participation in your account settings.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Employees of Bleau Consulting LLC (doing business as Budgit), its affiliates, subsidiaries, advertising and promotion agencies, and their immediate family members or household members are not eligible.</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            <strong>Void where prohibited.</strong> The Contest is void in jurisdictions where contests of skill with prizes are prohibited or require registration/bonding that Budgit has not obtained.
          </Typography>
        </Section>

        <Section id="no-purchase-necessary" title="3. No Purchase Necessary" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>NO PURCHASE OR PAYMENT IS NECESSARY TO ENTER OR WIN.</strong> A purchase or payment of any kind will not increase your chances of winning.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            While Budgit offers premium subscription features, participation in the Monthly Budgeting Challenge is available to all users with a free or premium account. Free users can:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Create budgets manually</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Track transactions manually</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Participate in the leaderboard on equal footing with premium users</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Premium features (such as automatic bank syncing and AI categorization) are conveniences that do not affect scoring or eligibility.
          </Typography>
        </Section>

        <Section id="contest-period" title="4. Contest Period" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Each Contest runs for one calendar month:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Start:</strong> 12:00:00 AM UTC on the first day of the month</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>End:</strong> 11:59:59 PM UTC on the last day of the month</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            For example, the {currentMonth} Contest runs from {currentMonth} 1 at 12:00:00 AM UTC through the last day of {currentMonth} at 11:59:59 PM UTC.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Sponsor reserves the right to modify, suspend, or cancel any Contest period with reasonable notice posted within the app.
          </Typography>
        </Section>

        <Section id="how-to-enter" title="5. How to Enter" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Participation is optional and off by default. New accounts are not entered into the Contest until you join from Discover (tap Join Contest and accept these Official Rules) and meet the eligibility requirements above.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            To participate during a contest period:
          </Typography>
          <List sx={{ listStyleType: 'decimal', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Create or maintain a Budgit account in good standing.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Open Discover and tap Join Contest.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Review and accept these Official Rules. Joining records your acceptance and enables leaderboard participation (Leaderboard Opt-Out is turned off).</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Use the app during the contest period. Rank is period XP as described in Section 6.</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            You may stop participating at any time by enabling Leaderboard Opt-Out in Profile or Account settings. That setting is a later control, not the way you join. Opting out removes you from public leaderboard display for future ranking; see the <Link href="/privacy">Privacy Policy</Link> for what may remain visible from prior activity.
          </Typography>
        </Section>

        <Section id="scoring" title="6. Scoring & Judging" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Scoring is entirely server-authoritative. Users cannot submit or manipulate scores. Rank is calculated by Budgit's servers from recorded XP events.</strong>
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Ranking method:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Rank is determined by period XP for the contest period. Period XP is the sum of experience points Budgit's servers award to the participant during that period under the then-current XP schedule shown in the app, after any revocations of those awards. A participant with zero period XP is not ranked.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Period XP may be awarded for budgeting activity (including staying under budget and related budget actions) and for other in-app actions that award XP during the period. Those other actions include, without limitation, first-use milestones, referrals, family invites, Daily Win, receipt actions, and profile or setup actions, as those awards exist from time to time. The XP schedule, amounts, multipliers, and which actions award period XP may change. The leaderboard uses the awards actually granted (and not revoked) for that period.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Sponsor may add, remove, or reweight XP-awarding actions. These Official Rules do not freeze the XP schedule.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2, mb: 2, fontWeight: 600 }}>
            Ranking Tiebreakers (applied in this order when period XP is tied):
          </Typography>
          <List sx={{ listStyleType: 'decimal', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Highest period XP (primary).</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Longest current streak (secondary).</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Earliest Official Rules acceptance time for the contest period (earlier wins; a participant with no recorded acceptance sorts last among a tie).</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">User ID (stable ordering).</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Disqualification: Editing an approved budget during its active period may disqualify you from that contest period, as described in Section 9. Disqualified participants are excluded from that period's ranking.
          </Typography>
        </Section>

        <Section id="prizes" title="7. Prizes" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, p: 2, backgroundColor: 'rgba(255, 152, 0, 0.1)', borderRadius: 2, border: '1px solid rgba(255, 152, 0, 0.3)' }}>
            <strong>⚠️ NO GUARANTEE OF PRIZES:</strong> Prizes are offered at Sponsor's sole discretion. Sponsor reserves the right to modify prize values, substitute prizes of equal or greater value, reduce the number of prizes, or discontinue the prize program entirely at any time, with or without notice. Participation does not guarantee a prize.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            The following prizes may be awarded each month (subject to change or discontinuation at Sponsor's sole discretion):
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>1st Place:</strong> Up to $100 USD cash prize (if offered)</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Additional prize tiers (2nd, 3rd place) may be added in future contest periods. Sponsor may also choose not to award any prizes for any given period.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Prize Value: If prizes are awarded, approximate retail value (ARV) of total prizes per contest period: up to $100 USD.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            A first-place ranking is not itself a payment. If a prize is offered and awarded, Sponsor must approve the award, the winner must claim it and complete any required identity and payout onboarding, and Sponsor then initiates the transfer.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Prize Fulfillment: Prizes, if awarded, are paid by electronic transfer via Stripe Connect or a successor payment processor, or another electronic method at Sponsor's sole discretion. Winners must provide valid payment information and complete any required onboarding to receive prizes. Prizes not claimed within 60 days of notification may be forfeited. Sponsor is under no obligation to reissue forfeited prizes.
          </Typography>
        </Section>

        <Section id="winner-selection" title="8. Winner Selection & Notification" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Winners are determined based on final leaderboard standings as of 11:59:59 PM UTC on the last day of each contest period.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Notification:</strong> Winners will be notified via email within 7 days of the contest end date using the email address associated with their Budgit account.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Verification:</strong> Potential winners may be required to verify eligibility by providing government-issued identification and completing an affidavit of eligibility.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Response Required:</strong> Winners must respond within 14 days of notification. Failure to respond may result in prize forfeiture and selection of an alternate winner.</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="disqualification" title="9. Disqualification" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Participants may be disqualified from the current contest period for:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Budget Manipulation:</strong> Editing an approved budget during its active period. Once approved, budgets should remain unchanged to ensure fair competition.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Account Fraud:</strong> Creating multiple accounts, using false identity information, or any form of account manipulation.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Data Manipulation:</strong> Any attempt to falsify transactions, manipulate bank connections, or otherwise game the scoring system.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Terms Violation:</strong> Violation of Budgit's <Link href="/terms">Terms of Service</Link>.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>Flagged Activity:</strong> Any behavior flagged by our automated fraud detection systems may result in manual review and potential disqualification.</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            Disqualified participants will be removed from the leaderboard for that period. Previous contest wins are not affected unless fraud is subsequently discovered.
          </Typography>
        </Section>

        <Section id="taxes" title="10. Taxes" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>All federal, state, and local taxes on prizes are the sole responsibility of the winner.</strong>
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            For U.S. residents:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Prize winnings of $600 or more in a calendar year require IRS Form W-9 and may result in Form 1099-MISC reporting.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Winners are responsible for reporting all prize income regardless of whether a 1099 is issued.</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            For non-U.S. residents, applicable withholding taxes may apply. Consult a tax professional regarding your obligations.
          </Typography>
        </Section>

        <Section id="general-conditions" title="11. General Conditions" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Sole Administrator & Distributor
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Bleau Consulting LLC, doing business as Budgit, is the sole administrator, operator, and distributor of the Contest and any prizes. No third parties are authorized to conduct, promote, or distribute prizes on behalf of the Contest. All decisions regarding Contest administration, eligibility, scoring, disqualification, and prize distribution are made exclusively by Sponsor and are final and binding.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Participant Acknowledgments
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            By participating, you acknowledge and agree that:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You are bound by these Official Rules and all decisions of Sponsor, which are final and binding in all matters.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1"><strong>No prizes are guaranteed.</strong> Prizes may be modified, reduced, or discontinued at any time at Sponsor's sole discretion.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You release Sponsor, its officers, directors, employees, agents, and affiliates from any and all liability arising from participation in the Contest or acceptance of any prizes.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">You grant Sponsor the right to use your first name, last initial, and general location (city/state) for winner announcements and promotional purposes without additional compensation.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Sponsor may, at its sole discretion, disqualify any participant for any reason.</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            <strong>Modifications & Cancellation:</strong> Sponsor reserves the absolute right to modify, suspend, or cancel the Contest, these rules, prize values, or any aspect of the program at any time, for any reason, with or without notice. This includes the right to discontinue the Contest program entirely. Any changes will be posted within the app when practicable.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            <strong>Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, Sponsor is not responsible for: (a) lost, late, incomplete, or misdirected entries; (b) technical failures of any kind; (c) any injury or damage to persons or property resulting from participation; (d) any printing, typographical, or technical errors; (e) any failure to award prizes for any reason. IN NO EVENT SHALL SPONSOR'S TOTAL LIABILITY EXCEED THE VALUE OF THE PRIZE AT ISSUE.
          </Typography>
        </Section>

        <Section id="privacy" title="12. Privacy" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Information collected from participants is subject to Sponsor's <Link href="/privacy">Privacy Policy</Link>.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            For leaderboard display:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Your first name and last initial are displayed publicly on the leaderboard.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Your profile image (if set) may be displayed.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Your score and streak are visible to other participants.</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2, mb: 2 }}>
            To opt out of public display, enable "Leaderboard Opt-Out" in your Profile settings.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Leaderboard display is controlled by your Leaderboard Opt-Out setting; social features remain off by default for new accounts as described in the Privacy Policy.
          </Typography>
        </Section>

        <Section id="disputes" title="13. Disputes" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Governing Law.</strong> These Official Rules and any dispute arising out of or relating to the Contest are governed by the laws of the State of Kansas, without regard to conflict of law principles, except that the Federal Arbitration Act governs the interpretation and enforcement of the arbitration agreement below.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Informal Resolution.</strong> Before filing a claim, contact support@budgit.app with a brief description of the dispute and the relief sought. If we cannot resolve it within 30 days, either party may proceed as below.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Binding Arbitration.</strong> Except for the Excluded Claims below, any dispute, claim, or controversy arising out of or relating to the Contest or these Official Rules will be resolved by binding individual arbitration administered by the American Arbitration Association ("AAA") under its Consumer Arbitration Rules. The arbitration will be conducted by a single arbitrator. The seat of arbitration is Johnson County, Kansas. You may choose to appear by video, phone, or in writing where the AAA rules allow.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Class Action Waiver.</strong> YOU AND SPONSOR AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one person's claims or preside over any form of representative or class proceeding.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Excluded Claims.</strong> Either party may bring an individual action in small-claims court if the claim qualifies. Either party may seek injunctive or other equitable relief in a court of competent jurisdiction to protect intellectual property rights or stop unauthorized access or abuse. If the class-action waiver is found unenforceable as to a particular claim, that claim (and only that claim) must proceed in court, not arbitration. Subject to the foregoing and any mandatory consumer protections that apply in your place of residence, court proceedings permitted by this section must be brought in the state or federal courts located in Johnson County, Kansas.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <strong>Opt-Out.</strong> You may opt out of this arbitration agreement within 30 days of first accepting these Official Rules (or of first accepting a version that added this section) by emailing support@budgit.app with subject "Contest Arbitration Opt-Out" and your account email. Opting out does not affect other Contest terms.
          </Typography>
        </Section>

        <Section id="sponsor" title="14. Sponsor" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            The Contest is sponsored by:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600, whiteSpace: 'pre-line' }}>
            {`Bleau Consulting LLC, doing business as Budgit
11985 S. Monticello Terrace
Olathe, KS 66061
support@budgit.app`}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            For questions about these rules or the Contest, contact us at <Link href="mailto:support@budgit.app">support@budgit.app</Link>.
          </Typography>
        </Section>

          </Grid>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <SideMenu />
          </Grid>
        </Grid>

      </Container>
    </Box>
  );
};

export default ContestRules;

